import { useState, ChangeEvent, FormEvent } from 'react';

import { ResponseCSOSHeaders, csosUserLifecycleApi, csosUserOktaSearchApi } from '../common';
import { LifecycleAction, UserSearchResult } from './userDirectoryTypes';

// Mocked global context headers matching your specification
const mockHeaders: ResponseCSOSHeaders = {
  'x-csrf-csos-token': 'extracted-token-from-your-state',
  'authorization': 'extracted-auth-from-your-state',
};

export function useUserDirectory() {
  const [emailQuery, setEmailQuery] = useState<string>('');
  const [searchResult, setSearchResult] = useState<UserSearchResult | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [actionSuccess, setActionSuccess] = useState<string>('');

  const clearMessages = () => {
    setError('');
    setActionSuccess('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailQuery(e.target.value);
    if (error) setError('');
    if (actionSuccess) setActionSuccess('');
  };

  const handleSearchSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const cleanEmail = emailQuery.trim();

    if (!cleanEmail) {
      setError('Please enter an email address.');
      return;
    }

    setIsLoading(true);
    clearMessages();
    setSearchResult(null);

    try {
      const response = await csosUserOktaSearchApi(mockHeaders, cleanEmail);
      const userData: UserSearchResult = response.data;

      if (!userData) {
        setError(`No user record found for email: ${cleanEmail}. Please reach out to the CSOS Technical Team for assistance.`);
        return;
      }

      setSearchResult(userData);
      if (userData.status === 'DEPROVISIONED') {
        setActionSuccess(
          `User account "${userData.profile.firstName} ${userData.profile.lastName}" needs to be reactivated in Okta. Please reach out to the CSOS Technical Team for assistance.`
        );
      } else if (userData.status === 'PASSWORD_EXPIRED') {
        setActionSuccess('User password is expired. Please send user password reset email.');
      }
    } catch (err: any) {
      console.error('Search API Error:', err);
      setError((err.response?.data?.message.includes('no person record found') || err.message.includes('no person record found')) ? `No user record found for email: ${cleanEmail}. Please reach out to the CSOS Technical Team for assistance.` : err.response?.data?.message || err.message || 'An error occurred while fetching user data.');
    } finally {
      setIsLoading(false);
    }
  };

  /** Runs a lifecycle action (unsuspend/unlock/activate/reset-password) against the current search result, then refreshes it. */
  const runLifecycleAction = async (actionType: LifecycleAction, buildSuccessMessage: (fullName: string) => string) => {
    if (!searchResult) return;

    setIsLoading(true);
    clearMessages();

    try {
      const identifier = searchResult.profile.email;
      await csosUserLifecycleApi(mockHeaders).toggleUserLifecycle(actionType, identifier);

      const fullName = `${searchResult.profile.firstName} ${searchResult.profile.lastName}`;
      setActionSuccess(buildSuccessMessage(fullName));

      const updatedSearchResponse = await csosUserOktaSearchApi(mockHeaders, identifier);
      const updatedUser: UserSearchResult = updatedSearchResponse.data;
      setSearchResult((prev) => (prev ? { ...prev, status: updatedUser.status } : null));
    } catch (err: any) {
      console.error('Lifecycle Modification Error:', err);
      setError(err.response?.data?.message || err.message || `Failed to complete the "${actionType}" action.`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    emailQuery,
    searchResult,
    error,
    isLoading,
    actionSuccess,
    handleInputChange,
    handleSearchSubmit,
    runLifecycleAction,
  };
}
